import {sum} from 'BenchlingUtil';
import {TABLE_COLUMN} from 'notebook/tables/constants/TableSelectionConstants';
import {TableHeaderRowProps} from 'notebook/tables/core-ui/TableHeaderRow';
import {TableRowProps} from 'notebook/tables/core-ui/TableRow';
import {RowFillDown} from 'notebook/tables/store/logic/RowFillDownLogic';
import TableUtil from 'notebook/tables/TableUtil';

export interface TableCellAffixedProps {
  /** Whether the column is frozen. Default is false. */
  isAffixed?: boolean;
  /** Only enabled when a column is frozen and scrolled. Default is false. */
  showAffixedShadow?: boolean;
  /** Must be specified if isAffixed = true. */
  affixedLeftSpacing?: number;
  isLastFrozenColumn?: boolean;
}

type GetTableCellAffixedPropsParameters = {
  /** The column index of the cell */
  j: number;
  /** If this cell is the anchor of a merge, the max column of the merged region. */
  mergeMaxJ?: number;
} & Pick<
  TableRowProps,
  | 'sliceStart'
  | 'affixedColumnCount'
  | 'hasAnyFrozenColumns'
  | 'firstColumnWidth'
  | 'showAffixedShadow'
  | 'columnWidths'
>;

/** Make the props needed to determine if a TableCell should be affixed (part of a frozen column) */
export function getTableCellAffixedProps({
  j,
  mergeMaxJ,
  sliceStart,
  affixedColumnCount,
  hasAnyFrozenColumns,
  firstColumnWidth,
  showAffixedShadow: parentShowAffixedShadow,
  columnWidths,
}: GetTableCellAffixedPropsParameters): TableCellAffixedProps {
  const slicedJ = j - (sliceStart ?? 0);
  const isAffixed = Boolean(affixedColumnCount && slicedJ < affixedColumnCount);
  const affixedLeftSpacing =
    columnWidths != null ? (firstColumnWidth ?? 0) + sum(columnWidths.slice(sliceStart ?? 0, j)) : 0;
  const lastFrozenJ = (sliceStart ?? 0) + (affixedColumnCount ?? 0) - 1;
  const showAffixedShadow = Boolean(
    parentShowAffixedShadow && affixedColumnCount && slicedJ === affixedColumnCount - 1
  );
  const mergeEndsAtLastFrozen = mergeMaxJ != null && mergeMaxJ === lastFrozenJ && j <= lastFrozenJ;
  const isLastFrozenColumn = Boolean(
    hasAnyFrozenColumns && affixedColumnCount && (slicedJ === affixedColumnCount - 1 || mergeEndsAtLastFrozen)
  );
  return {
    isAffixed,
    showAffixedShadow,
    affixedLeftSpacing,
    isLastFrozenColumn,
  };
}

export type AllowDeleteColumnParameters = {
  columnIndex: number;
} & Pick<TableHeaderRowProps, 'canAddRemoveColumns' | 'getSelectionRange' | 'table'>;

/**
 * Determines if the selected column can be deleted from the table.
 *
 * Uses ColumnConstraint.isColumnUserConfigurable to determine if the column can be deleted. If that field is not
 * present defaults to checking table.legacyFirstEditableColumnIndex.
 *
 * @returns whether the column can be deleted
 */
export function allowDeleteColumn({
  columnIndex,
  canAddRemoveColumns,
  getSelectionRange,
  table,
}: AllowDeleteColumnParameters): boolean {
  if (!canAddRemoveColumns) {
    return false;
  }

  const range = getSelectionRange(TABLE_COLUMN, columnIndex);
  if (range == null) {
    return false;
  }

  // If isColumnUserConfigurable is set on any column constraints, it will be set on all of them, so we
  // will rely on it.
  let isColumnUserConfigurableSet = false;
  for (let i = range.minIndex; i <= range.maxIndex; i++) {
    const columnConstraint = table.columnConstraints?.[i];
    if (columnConstraint && columnConstraint.isColumnUserConfigurable != null) {
      isColumnUserConfigurableSet = true;
      if (!columnConstraint.isColumnUserConfigurable) {
        return false;
      }
    }
  }
  if (isColumnUserConfigurableSet) {
    return true;
  }

  // If isColumnUserConfigurable is not set (e.g. legacy results table), we fall back to the deprecated
  // legacyFirstEditableColumnIndex behavior
  const firstEditableIndex = TableUtil.legacyFirstEditableColumnIndex(table);
  return firstEditableIndex == null || (range != null && range.minIndex >= firstEditableIndex);
}

export {isCellSelected} from 'notebook/tables/core-ui/TableRowUtils';

export function cellFillProps(
  rowModel: RowFillDown,
  j: number
): {
  isFillDownBoxLeftEdge: boolean;
  isFillDownBoxRightEdge: boolean;
  isFillDownBoxTopEdge: boolean;
  isFillDownBoxBottomEdge: boolean;
} {
  return {
    isFillDownBoxLeftEdge:
      rowModel.isFilledDown && (rowModel.fillMinJ === j || j === (rowModel.fillMaxJ || 0) + 1),
    isFillDownBoxRightEdge:
      rowModel.isFilledDown && (rowModel.fillMaxJ === j || j === (rowModel.fillMinJ || 0) - 1),
    isFillDownBoxTopEdge:
      rowModel.isFillDownBoxTopEdge && (rowModel.fillMinJ || 0) <= j && j <= (rowModel.fillMaxJ || 0),
    isFillDownBoxBottomEdge:
      rowModel.isFillDownBoxBottomEdge && (rowModel.fillMinJ || 0) <= j && j <= (rowModel.fillMaxJ || 0),
  };
}
